import { getChartDefaultOptions } from "./ChartJsConfig";
import { useTheme } from "@mui/material/styles";
import appDefaultTheme from "@/config/muiTheme";
import { getChartTextColor } from "./chartTextColor";
import React, { useState, useEffect, useMemo } from "react";
import * as ReactChartJs from "react-chartjs-2";
import {
  getChartColors,
  makeUniqChartData,
  RPNumberFormat,
} from "@/helpers/utils";
import { getDatasetDefaultOptions } from "./ChartJsConfig";

type ChartRow = Record<string, string | number | null | undefined>;

type ChartGroup = {
  title?: string;
  data: ChartRow[];
};

type DataFormat = {
  prefix: string;
  suffix: string;
};

type DataFormats = Record<string, DataFormat>;

type StackedBarChartProps = {
  data?: ChartGroup[];
  option?: Record<string, unknown>;
  scales?: Record<string, unknown>;
  formats?: DataFormats | null;
};

type TooltipItem = {
  datasetIndex: number;
  value?: string | number | null;
};

type TooltipData = {
  datasets: Array<{
    label: string;
  }>;
};

const { Bar } = ReactChartJs as unknown as {
  Bar: React.ComponentType<Record<string, unknown>>;
};
const StackedBarChart = ({
  data = [],
  option = {},
  scales = {},
  formats = null,
}: StackedBarChartProps) => {
  const providedTheme = useTheme();
  const theme = providedTheme.palette.neutral ? providedTheme : appDefaultTheme;
  const [chartData, setChartData] = useState<Record<string, unknown>>({});

  const labels = Object.keys(data?.[0]?.data?.[0] || {});

  const options = useMemo(
    () => ({
      ...getChartDefaultOptions(theme),
      tooltips: {
        ...getChartDefaultOptions(theme).tooltips,
        //intersect: false,
        callbacks: {
          label: (tooltipItem: TooltipItem, data: TooltipData) => {
            const { datasetIndex, value } = tooltipItem;

            const label = data.datasets[datasetIndex].label;

            const format = formats ? formats[labels[1]] : null;
            if (!value) return "";
            if (formats && format) {
              return `${label}: ${RPNumberFormat({
                prefix: format.prefix,
                suffix: format.suffix,
                thousand: ",",
              }).to(parseFloat(`${value}`))}`;
            }
            return `${label}: ${RPNumberFormat().to(parseFloat(`${value}`))}`;
          },
          title: (tooltipItem: Array<{ label: string }>) => {
            const label = tooltipItem[0].label;
            return label;
          },
        },
      },
      legend: {
        display: false,
        align: "center",
        position: "bottom",
        labels: {
          boxWidth: 10,
          fontColor: getChartTextColor(theme),
          padding: 20,
        },
      },
      plugins: {
        datalabels: {
          display: false,
        },
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            gridLines: {
              display: false,
            },
            ticks: {
              fontColor: getChartTextColor(theme),
              callback: (value: string) => {
                return value.slice(0, 7) + `${value.length > 7 ? ".." : ""}`;
              },
            },
          },
        ],
        yAxes: [
          {
            id: "y-axis-0",
            position: "left",
            stacked: true,
            ticks: {
              fontColor: getChartTextColor(theme),
              min: 0,
              maxTicksLimit: 5,
              callback: (value: string | number) => {
                const format = formats ? formats[labels[1]] : null;
                if (!value) return "";
                if (formats && format) {
                  return RPNumberFormat({
                    prefix: format.prefix,
                    suffix: format.suffix,
                    thousand: ",",
                  }).to(value);
                }
                return RPNumberFormat().to(value);
              },
            },
          },
        ],
        ...scales,
      },
      ...option,
    }),
    [option, scales, theme]
  );

  const getDataSet = () => {
    const datasets = Object.keys(data)?.map((key, i) => {
      const d = data[Number(key)];
      const formatData = makeUniqChartData(d.data, labels[0]).map((d) => ({
        x: d[labels[0]],
        y: d[labels[1]],
      }));

      let ds = {
        label: d?.title,
        data: formatData,
        yAxisID: "y-axis-0",
        ...getDatasetDefaultOptions(theme),
        backgroundColor: getChartColors(3)[i],
        hoverBackgroundColor: getChartColors(3)[i],
      };
      return ds;
    });

    return datasets;
  };

  useEffect(() => {
    const initData = {
      labels: getDataSet()[0].data ? getDataSet()[0].data.map((d) => d.x) : [],
      datasets: getDataSet(),
    };
    setChartData(initData);
  }, [data, theme]);

  return (
    <div className="area-chart">
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default StackedBarChart;
